'use client';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { TIME_SLOTS } from './bookingTypes';

interface Step2Props {
  selectedDate: string | null;
  selectedSlot: string | null;
  onSelect: (date: string, slot: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Unavailable days (0-indexed from month start)
const UNAVAILABLE_DAYS = new Set([1, 4, 8, 11, 15, 18, 22, 25]);

export default function Step2DateTime({ selectedDate, selectedSlot, onSelect, onNext, onBack }: Step2Props) {
  const today = new Date(2026, 4, 12); // May 12, 2026 — static to avoid hydration mismatch
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [localDate, setLocalDate] = useState<string | null>(selectedDate);
  const [localSlot, setLocalSlot] = useState<string | null>(selectedSlot);

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day: number) => {
    const isPast = new Date(viewYear, viewMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (isPast || UNAVAILABLE_DAYS.has(day)) return;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setLocalDate(dateStr);
    setLocalSlot(null);
  };

  const handleSlotClick = (slotId: string) => {
    setLocalSlot(slotId);
    if (localDate) onSelect(localDate, slotId);
  };

  const formatDisplayDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return `${MONTHS[m - 1]} ${d}, ${y}`;
  };

  const calendarCells: (number | null)[] = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  return (
    <div className="animate-slide-up">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div className="bg-card rounded-xl border border-border shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-700 text-foreground">
              {MONTHS[viewMonth]} {viewYear}
            </h3>
            <div className="flex gap-1">
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="Previous month">
                <ChevronLeft size={16} />
              </button>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="Next month">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map((d) => (
              <div key={`day-header-${d}`} className="text-center text-[11px] font-600 text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarCells.map((day, idx) => {
              if (!day) return <div key={`cal-empty-${idx}`} />;
              const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isPast = new Date(viewYear, viewMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const isUnavailable = UNAVAILABLE_DAYS.has(day);
              const isSelected = localDate === dateStr;
              const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

              return (
                <button
                  key={`cal-day-${day}`}
                  onClick={() => handleDayClick(day)}
                  disabled={isPast || isUnavailable}
                  className={`relative w-full aspect-square flex items-center justify-center rounded-lg text-sm font-500 transition-all ${
                    isSelected
                      ? 'bg-primary text-primary-foreground font-700'
                      : isPast || isUnavailable
                      ? 'text-muted-foreground/40 cursor-not-allowed'
                      : 'hover:bg-secondary hover:text-primary cursor-pointer'
                  } ${isToday && !isSelected ? 'ring-2 ring-primary/30 font-700' : ''}`}
                >
                  {day}
                  {!isPast && !isUnavailable && !isSelected && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-xs text-muted-foreground">Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
              <span className="text-xs text-muted-foreground">Unavailable</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary" />
              <span className="text-xs text-muted-foreground">Selected</span>
            </div>
          </div>
        </div>

        {/* Time Slots */}
        <div className="bg-card rounded-xl border border-border shadow-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={15} className="text-primary" />
            <h3 className="text-sm font-700 text-foreground">
              {localDate ? `Available Times — ${formatDisplayDate(localDate)}` : 'Select a date first'}
            </h3>
          </div>

          {!localDate ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Clock size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-500 text-muted-foreground">Pick a date on the calendar</p>
              <p className="text-xs text-muted-foreground mt-1">Available time slots will appear here</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot.id}
                  onClick={() => slot.available && handleSlotClick(slot.id)}
                  disabled={!slot.available}
                  className={`py-3 px-4 rounded-lg text-sm font-600 transition-all text-center ${
                    localSlot === slot.id
                      ? 'slot-selected'
                      : slot.available
                      ? 'slot-available' :'slot-unavailable'
                  }`}
                >
                  {slot.time}
                  {!slot.available && <span className="block text-[10px] font-400 mt-0.5 opacity-70">Booked</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary flex items-center gap-2">
          <ChevronLeft size={16} />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!localDate || !localSlot}
          className="btn-primary flex items-center gap-2"
        >
          Continue to Your Details
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}